import {
  VILNIUS_CATHEDRAL_COLLISION,
  VILNIUS_GEDIMINAS_CASTLE_COLLISION,
  VILNIUS_GRAND_DUKES_PALACE_COLLISION,
  VILNIUS_GATE_OF_DAWN_COLLISION,
  VILNIUS_UNIVERSITY_LIBRARY_COLLISION,
  VILNIUS_AIRPORT_TERMINAL_COLLISION,
  VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION,
  VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION,
  VILNIUS_GEDIMINO_9_COLLISION,
  VILNIUS_HALES_MARKET_COLLISION,
  VILNIUS_KABLYS_COLLISION,
  VILNIUS_LIETUVOS_BANKAS_COLLISION,
  VILNIUS_LUKISKES_PRISON_COLLISION,
  VILNIUS_LUKISKES_PRISON_FOOTPRINTS,
  VILNIUS_MINDAUGO_MAXIMA_COLLISION,
  VILNIUS_PRESIDENTIAL_PALACE_COLLISION,
  VILNIUS_RAILWAY_STATION_COLLISION,
  VILNIUS_BUS_STATION_COLLISION,
  VILNIUS_TV_TOWER_COLLISION,
  VILNIUS_TOWN_HALL_COLLISION,
  VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION,
} from "./landmarks.ts";

export type AuthoredInteriorId =
  | "vilnius-cathedral-nave"
  | "gediminas-castle-museum"
  | "palace-grand-dukes-museum"
  | "gate-of-dawn-chapel"
  | "vilnius-university-historic-library"
  | "presidential-palace-state-rooms"
  | "vilnius-airport-new-departures-hall"
  | "vilnius-airport-existing-terminals"
  | "gedimino-9-atrium"
  | "demokratine-mokykla-foyer"
  | "salomejos-neries-gimnazija-ground-floor"
  | "baltas-tiltas-food-hall"
  | "hales-market-hall"
  | "mindaugo-maxima-supermarket"
  | "kablys-main-hall"
  | "vilnius-town-hall-lobby"
  | "lietuvos-bankas-historic-vault"
  | "lukiskes-prison-cellblock"
  | "vilnius-railway-station-hall"
  | "vilnius-bus-station-hall"
  | "vilnius-tv-tower-lobby"
  | "vilnius-tv-tower-observation-deck";

export type InteriorAcousticProfile =
  | "cathedral-nave"
  | "airport-terminal"
  | "civic-lobby"
  | "food-hall"
  | "music-venue"
  | "railway-hall"
  | "bus-terminal"
  | "historic-bank"
  | "prison-cellblock"
  | "tower-lobby"
  | "tower-observation";

export interface InteriorLocalVolume {
  readonly centerLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly halfSizeM: Readonly<{ x: number; y: number; z: number }>;
}

export interface InteriorPointLightSpec {
  readonly id: string;
  readonly positionLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly color: number;
  readonly intensity: number;
  readonly distanceM: number;
}

export interface InteriorAmbientExclusionVolume {
  readonly centerLocalM: Readonly<{ x: number; z: number }>;
  readonly halfSizeM: Readonly<{ x: number; z: number }>;
}

export interface InteriorAmbientOccupantSpec {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly positionLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly activity: "working" | "resting";
  readonly clothing: Readonly<{ coat: number; accent: number }>;
}

export interface AuthoredInteriorDefinition {
  readonly id: AuthoredInteriorId;
  readonly label: string;
  readonly landmarkId: string;
  readonly origin: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationYRadians: number;
  readonly volumes: readonly InteriorLocalVolume[];
  readonly cameraObstructionIds: readonly string[];
  readonly acousticProfile: InteriorAcousticProfile;
  readonly exteriorLightScale: number;
  readonly exposureScale: number;
  /** Optional close third-person framing for compact authored rooms. */
  readonly cameraDistanceM?: number;
  readonly pointLights: readonly InteriorPointLightSpec[];
  /** Outdoor pedestrian-graph areas that must not seed interior crowds. */
  readonly ambientExclusionVolumes?: readonly InteriorAmbientExclusionVolume[];
  /** Source-bounded, non-interactive public activity staged inside the room. */
  readonly ambientOccupants?: readonly InteriorAmbientOccupantSpec[];
}

function landmarkObstructionId(
  landmark: Readonly<{
    tileId: string;
    buildingId: string;
  }>,
): string {
  return `building:${landmark.tileId}:${landmark.buildingId}`;
}

const UNIVERSITY_LIBRARY_INTERIOR_ANGLE = -49.5 * Math.PI / 180;

function universityLibraryInteriorPoint(
  longitudinal: number,
  lateral: number,
  y: number,
): Readonly<{ x: number; y: number; z: number }> {
  const tangentX = Math.cos(UNIVERSITY_LIBRARY_INTERIOR_ANGLE);
  const tangentY = Math.sin(UNIVERSITY_LIBRARY_INTERIOR_ANGLE);
  const normalX = -tangentY;
  const normalY = tangentX;
  const blenderX = -49.6 + tangentX * longitudinal + normalX * lateral;
  const blenderY = -28.8 + tangentY * longitudinal + normalY * lateral;
  return { x: blenderX, y, z: -blenderY };
}

/**
 * PROVISIONAL — USER TUNING REQUIRED.
 *
 * These source-model-aligned volumes cover only the authored public
 * interiors. They deliberately stop at their closed side rooms and upper
 * floors. Detection uses the player's grounded foot position; camera and
 * audio presentation consume the resulting stable interior ID.
 */
export const AUTHORED_INTERIORS: readonly AuthoredInteriorDefinition[] =
  Object.freeze([
    Object.freeze({
      id: "vilnius-cathedral-nave",
      label: "Vilnius Cathedral nave, St Casimir Chapel and crypts",
      landmarkId: VILNIUS_CATHEDRAL_COLLISION.landmarkId,
      origin: VILNIUS_CATHEDRAL_COLLISION.origin,
      rotationYRadians: VILNIUS_CATHEDRAL_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: -2.5, y: 2.1, z: 0 },
          halfSizeM: { x: 23.8, y: 2.1, z: 26.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 29.7, y: 4.8, z: 20.8 },
          halfSizeM: { x: 6.65, y: 5.0, z: 6.65 },
        }),
        Object.freeze({
          centerLocalM: { x: 25.6, y: -0.9, z: 23.45 },
          halfSizeM: { x: 1.55, y: 2.0, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 29.7, y: -0.8, z: 13.3 },
          halfSizeM: { x: 5.6, y: 1.75, z: 14.1 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_CATHEDRAL_COLLISION),
      ]),
      acousticProfile: "cathedral-nave",
      exteriorLightScale: 0.32,
      exposureScale: 1.08,
      pointLights: Object.freeze([
        Object.freeze({
          id: "west-nave-fill",
          positionLocalM: { x: -15, y: 7.8, z: 0 },
          color: 0xffe2b2,
          intensity: 62,
          distanceM: 32,
        }),
        Object.freeze({
          id: "crossing-fill",
          positionLocalM: { x: 3, y: 8.8, z: 0 },
          color: 0xffedcf,
          intensity: 74,
          distanceM: 34,
        }),
        Object.freeze({
          id: "altar-warmth",
          positionLocalM: { x: 18, y: 5.4, z: 0 },
          color: 0xf4bd62,
          intensity: 54,
          distanceM: 20,
        }),
        Object.freeze({
          id: "casimir-sarcophagus",
          positionLocalM: { x: 29.7, y: 6.8, z: 24.2 },
          color: 0xf0c070,
          intensity: 78,
          distanceM: 16,
        }),
        Object.freeze({
          id: "crypt-royal-chamber",
          positionLocalM: { x: 29.7, y: 0.15, z: -0.2 },
          color: 0xd1b28b,
          intensity: 34,
          distanceM: 14,
        }),
      ]),
    }),
    Object.freeze({
      id: "gediminas-castle-museum",
      label: "Gediminas Castle Tower museum and observation deck",
      landmarkId: VILNIUS_GEDIMINAS_CASTLE_COLLISION.landmarkId,
      origin: VILNIUS_GEDIMINAS_CASTLE_COLLISION.origin,
      rotationYRadians: VILNIUS_GEDIMINAS_CASTLE_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: -0.91, y: 10.7, z: 6.17 },
          halfSizeM: { x: 7.0, y: 10.8, z: 7.0 },
        }),
        Object.freeze({
          centerLocalM: { x: -0.91, y: 22.25, z: 6.17 },
          halfSizeM: { x: 7.3, y: 0.75, z: 7.3 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_GEDIMINAS_CASTLE_COLLISION),
      ]),
      acousticProfile: "tower-lobby",
      exteriorLightScale: 0.25,
      exposureScale: 1.08,
      cameraDistanceM: 2.8,
      pointLights: Object.freeze([
        ...([2.8, 8.2, 13.6, 18.8] as const).map((y, index) =>
          Object.freeze({
            id: `museum-level-${index + 1}`,
            positionLocalM: { x: -0.91, y, z: 6.17 },
            color: index === 2 ? 0xf2c071 : 0xffddb0,
            intensity: 42,
            distanceM: 12,
          })
        ),
      ]),
    }),
    Object.freeze({
      id: "palace-grand-dukes-museum",
      label: "Palace of the Grand Dukes museum routes",
      landmarkId: VILNIUS_GRAND_DUKES_PALACE_COLLISION.landmarkId,
      origin: VILNIUS_GRAND_DUKES_PALACE_COLLISION.origin,
      rotationYRadians: VILNIUS_GRAND_DUKES_PALACE_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.8, z: 45.5 },
          halfSizeM: { x: 61.5, y: 2.9, z: 9.2 },
        }),
        Object.freeze({
          centerLocalM: { x: -8, y: -1.55, z: 45.5 },
          halfSizeM: { x: 39.5, y: 1.7, z: 8.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 9.3, z: 45.5 },
          halfSizeM: { x: 61.5, y: 3.0, z: 9.2 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_GRAND_DUKES_PALACE_COLLISION),
      ]),
      acousticProfile: "historic-bank",
      exteriorLightScale: 0.26,
      exposureScale: 1.05,
      cameraDistanceM: 3.1,
      pointLights: Object.freeze([
        Object.freeze({ id: "archaeology", positionLocalM: { x: -8, y: -0.8, z: 45.5 }, color: 0xd6a96f, intensity: 85, distanceM: 34 }),
        Object.freeze({ id: "ground-route-west", positionLocalM: { x: -30, y: 4.2, z: 45.5 }, color: 0xffd9a3, intensity: 88, distanceM: 30 }),
        Object.freeze({ id: "ground-route-east", positionLocalM: { x: 30, y: 4.2, z: 45.5 }, color: 0xffd9a3, intensity: 88, distanceM: 30 }),
        Object.freeze({ id: "state-rooms-west", positionLocalM: { x: -30, y: 11.0, z: 45.5 }, color: 0xffd5a0, intensity: 92, distanceM: 30 }),
        Object.freeze({ id: "throne-room", positionLocalM: { x: 45, y: 11.0, z: 47.5 }, color: 0xf4b975, intensity: 100, distanceM: 26 }),
      ]),
    }),
    Object.freeze({
      id: "gate-of-dawn-chapel",
      label: "Gate of Dawn chapel and pilgrim stair",
      landmarkId: VILNIUS_GATE_OF_DAWN_COLLISION.landmarkId,
      origin: VILNIUS_GATE_OF_DAWN_COLLISION.origin,
      rotationYRadians: VILNIUS_GATE_OF_DAWN_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: -5.55, y: 4.0, z: 0 },
          halfSizeM: { x: 2.2, y: 4.1, z: 9.7 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 11.8, z: 0 },
          halfSizeM: { x: 7.7, y: 3.6, z: 9.7 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_GATE_OF_DAWN_COLLISION),
      ]),
      acousticProfile: "cathedral-nave",
      exteriorLightScale: 0.2,
      exposureScale: 1.04,
      cameraDistanceM: 2.6,
      pointLights: Object.freeze([
        Object.freeze({ id: "pilgrim-stair", positionLocalM: { x: -5.55, y: 5.4, z: 0 }, color: 0xf1bd79, intensity: 42, distanceM: 16 }),
        Object.freeze({ id: "chapel-icon", positionLocalM: { x: 0, y: 12.4, z: -7.5 }, color: 0xf5bb57, intensity: 74, distanceM: 18 }),
        Object.freeze({ id: "chapel-fill", positionLocalM: { x: 0, y: 12.0, z: 2.5 }, color: 0xffdfb5, intensity: 54, distanceM: 18 }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-university-historic-library",
      label: "Vilnius University historic library halls",
      landmarkId: VILNIUS_UNIVERSITY_LIBRARY_COLLISION.landmarkId,
      origin: VILNIUS_UNIVERSITY_LIBRARY_COLLISION.origin,
      rotationYRadians: VILNIUS_UNIVERSITY_LIBRARY_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        ...([-24, -12, 0, 12, 24] as const).map((longitudinal) =>
          Object.freeze({
            centerLocalM: universityLibraryInteriorPoint(longitudinal, 0, 2.4),
            halfSizeM: { x: 7.5, y: 2.5, z: 7.5 },
          })
        ),
        ...([-24, -12, 0, 12, 24] as const).map((longitudinal) =>
          Object.freeze({
            centerLocalM: universityLibraryInteriorPoint(longitudinal, 0, 8.4),
            halfSizeM: { x: 7.5, y: 3.2, z: 7.5 },
          })
        ),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_UNIVERSITY_LIBRARY_COLLISION),
      ]),
      acousticProfile: "historic-bank",
      exteriorLightScale: 0.3,
      exposureScale: 1.08,
      cameraDistanceM: 3.0,
      pointLights: Object.freeze([
        Object.freeze({ id: "smuglevicius-hall", positionLocalM: universityLibraryInteriorPoint(-20, 0, 3.7), color: 0xffd0a0, intensity: 76, distanceM: 21 }),
        Object.freeze({ id: "lelewel-hall", positionLocalM: universityLibraryInteriorPoint(-2, 0, 3.7), color: 0xf0c48e, intensity: 70, distanceM: 20 }),
        Object.freeze({ id: "white-hall-west", positionLocalM: universityLibraryInteriorPoint(-8, 0, 9.4), color: 0xffe5c4, intensity: 82, distanceM: 22 }),
        Object.freeze({ id: "white-hall-east", positionLocalM: universityLibraryInteriorPoint(14, 0, 9.4), color: 0xffe5c4, intensity: 82, distanceM: 22 }),
      ]),
    }),
    Object.freeze({
      id: "presidential-palace-state-rooms",
      label: "Presidential Palace public state rooms",
      landmarkId: VILNIUS_PRESIDENTIAL_PALACE_COLLISION.landmarkId,
      origin: VILNIUS_PRESIDENTIAL_PALACE_COLLISION.origin,
      rotationYRadians:
        VILNIUS_PRESIDENTIAL_PALACE_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        // Ground floor: courtyard threshold, press room, delegation lounge,
        // dining/maps rooms and the full ceremonial stair volume.
        Object.freeze({
          centerLocalM: { x: 6.0, y: 2.8, z: -42.0 },
          halfSizeM: { x: 2.5, y: 2.8, z: 2.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 3.0, y: 2.8, z: -39.0 },
          halfSizeM: { x: 2.0, y: 2.8, z: 0.7 },
        }),
        Object.freeze({
          centerLocalM: { x: -1.0, y: 2.8, z: -36.0 },
          halfSizeM: { x: 7.0, y: 2.8, z: 2.5 },
        }),
        Object.freeze({
          centerLocalM: { x: -4.0, y: 2.8, z: -32.25 },
          halfSizeM: { x: 1.5, y: 2.8, z: 1.4 },
        }),
        Object.freeze({
          centerLocalM: { x: -9.0, y: 2.8, z: -28.0 },
          halfSizeM: { x: 8.5, y: 2.8, z: 3.0 },
        }),
        Object.freeze({
          centerLocalM: { x: -7.0, y: 2.8, z: -23.5 },
          halfSizeM: { x: 1.5, y: 2.8, z: 1.6 },
        }),
        Object.freeze({
          centerLocalM: { x: -5.2, y: 2.8, z: -18.0 },
          halfSizeM: { x: 7.5, y: 2.8, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: -4.0, y: 2.8, z: -11.0 },
          halfSizeM: { x: 1.5, y: 2.8, z: 3.1 },
        }),
        Object.freeze({
          centerLocalM: { x: -0.5, y: 3.8, z: -3.3 },
          halfSizeM: { x: 9.7, y: 3.8, z: 5.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 0.0, y: 2.8, z: 2.5 },
          halfSizeM: { x: 1.5, y: 2.8, z: 2.6 },
        }),
        Object.freeze({
          centerLocalM: { x: 1.5, y: 2.8, z: 9.0 },
          halfSizeM: { x: 9.0, y: 2.8, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 2.0, y: 2.8, z: 14.5 },
          halfSizeM: { x: 1.5, y: 2.8, z: 1.6 },
        }),
        Object.freeze({
          centerLocalM: { x: 3.0, y: 2.8, z: 19.5 },
          halfSizeM: { x: 7.5, y: 2.8, z: 3.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 7.0, y: 2.8, z: 25.0 },
          halfSizeM: { x: 1.5, y: 2.8, z: 2.1 },
        }),
        Object.freeze({
          centerLocalM: { x: 9.0, y: 2.8, z: 31.5 },
          halfSizeM: { x: 11.5, y: 2.8, z: 4.5 },
        }),
        // State floor: connected rooms and links from the meeting rooms to
        // the Negotiation, Blue, Green and White Halls.
        Object.freeze({
          centerLocalM: { x: 0.0, y: 10.4, z: -36.0 },
          halfSizeM: { x: 6.5, y: 3.2, z: 2.5 },
        }),
        Object.freeze({
          centerLocalM: { x: -7.0, y: 10.4, z: -32.25 },
          halfSizeM: { x: 1.5, y: 3.2, z: 1.4 },
        }),
        Object.freeze({
          centerLocalM: { x: -9.0, y: 10.4, z: -28.0 },
          halfSizeM: { x: 8.5, y: 3.2, z: 3.0 },
        }),
        Object.freeze({
          centerLocalM: { x: -5.2, y: 10.4, z: -18.0 },
          halfSizeM: { x: 7.5, y: 3.2, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: -1.5, y: 10.4, z: -8.0 },
          halfSizeM: { x: 9.0, y: 3.2, z: 3.5 },
        }),
        Object.freeze({
          centerLocalM: { x: -2.1, y: 10.4, z: -2.75 },
          halfSizeM: { x: 0.9, y: 3.2, z: 1.8 },
        }),
        Object.freeze({
          centerLocalM: { x: 1.0, y: 10.4, z: 5.0 },
          halfSizeM: { x: 9.5, y: 3.2, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 2.0, y: 10.4, z: 10.0 },
          halfSizeM: { x: 1.5, y: 3.2, z: 1.1 },
        }),
        Object.freeze({
          centerLocalM: { x: 2.5, y: 10.4, z: 15.0 },
          halfSizeM: { x: 8.5, y: 3.2, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 4.5, y: 10.4, z: 23.0 },
          halfSizeM: { x: 8.5, y: 3.2, z: 3.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 9.0, y: 10.4, z: 32.0 },
          halfSizeM: { x: 12.0, y: 3.2, z: 4.5 },
        }),
        Object.freeze({
          centerLocalM: { x: -5.5, y: 10.4, z: -23.5 },
          halfSizeM: { x: 3.5, y: 3.2, z: 4.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 0.5, y: 10.4, z: 0.0 },
          halfSizeM: { x: 4.0, y: 3.2, z: 4.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 6.0, y: 10.4, z: 27.0 },
          halfSizeM: { x: 4.0, y: 3.2, z: 3.0 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_PRESIDENTIAL_PALACE_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.24,
      exposureScale: 1.03,
      cameraDistanceM: 3.0,
      pointLights: Object.freeze([
        Object.freeze({
          id: "courtyard-vestibule",
          positionLocalM: { x: 5.0, y: 4.7, z: -3.0 },
          color: 0xffd7a0,
          intensity: 70,
          distanceM: 16,
        }),
        Object.freeze({
          id: "press-room",
          positionLocalM: { x: -5.2, y: 5.7, z: -18.0 },
          color: 0xffe7c6,
          intensity: 85,
          distanceM: 16,
        }),
        Object.freeze({
          id: "delegations-lounge",
          positionLocalM: { x: -9.0, y: 5.8, z: -28.0 },
          color: 0xffd7a0,
          intensity: 70,
          distanceM: 15,
        }),
        Object.freeze({
          id: "state-dining",
          positionLocalM: { x: 1.5, y: 5.9, z: 9.0 },
          color: 0xffd39a,
          intensity: 90,
          distanceM: 18,
        }),
        Object.freeze({
          id: "maps-room",
          positionLocalM: { x: 3.0, y: 5.8, z: 19.5 },
          color: 0xffddb1,
          intensity: 70,
          distanceM: 15,
        }),
        Object.freeze({
          id: "upper-meeting",
          positionLocalM: { x: -5.2, y: 12.3, z: -18.0 },
          color: 0xffdfb5,
          intensity: 80,
          distanceM: 16,
        }),
        Object.freeze({
          id: "presidential-suite",
          positionLocalM: { x: -1.5, y: 12.3, z: -8.0 },
          color: 0xffd7a0,
          intensity: 70,
          distanceM: 15,
        }),
        Object.freeze({
          id: "negotiation-hall",
          positionLocalM: { x: 1.0, y: 12.4, z: 5.0 },
          color: 0xffe5bf,
          intensity: 95,
          distanceM: 19,
        }),
        Object.freeze({
          id: "blue-hall",
          positionLocalM: { x: -1.2, y: 12.4, z: 15.0 },
          color: 0xffd6a1,
          intensity: 75,
          distanceM: 15,
        }),
        Object.freeze({
          id: "green-hall",
          positionLocalM: { x: 6.4, y: 12.4, z: 15.0 },
          color: 0xffd6a1,
          intensity: 75,
          distanceM: 15,
        }),
        Object.freeze({
          id: "white-hall-west",
          positionLocalM: { x: 2.5, y: 12.5, z: 32.0 },
          color: 0xffedcf,
          intensity: 100,
          distanceM: 18,
        }),
        Object.freeze({
          id: "white-hall-centre",
          positionLocalM: { x: 9.0, y: 12.5, z: 32.0 },
          color: 0xffedcf,
          intensity: 110,
          distanceM: 19,
        }),
        Object.freeze({
          id: "white-hall-east",
          positionLocalM: { x: 15.5, y: 12.5, z: 32.0 },
          color: 0xffedcf,
          intensity: 100,
          distanceM: 18,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-town-hall-lobby",
      label: "Vilnius Town Hall lobby",
      landmarkId: VILNIUS_TOWN_HALL_COLLISION.landmarkId,
      origin: VILNIUS_TOWN_HALL_COLLISION.origin,
      rotationYRadians: VILNIUS_TOWN_HALL_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: -8.7 },
          halfSizeM: { x: 14.85, y: 2.2, z: 5.35 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: -13.2 },
          halfSizeM: { x: 2.35, y: 2.2, z: 1.15 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: -0.1 },
          halfSizeM: { x: 3.95, y: 2.2, z: 3.05 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_TOWN_HALL_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.44,
      exposureScale: 1.04,
      pointLights: Object.freeze([
        Object.freeze({
          id: "west-vault-fill",
          positionLocalM: { x: -7.6, y: 3.8, z: -8.1 },
          color: 0xffd6a0,
          intensity: 42,
          distanceM: 17,
        }),
        Object.freeze({
          id: "east-vault-fill",
          positionLocalM: { x: 7.6, y: 3.8, z: -8.1 },
          color: 0xffd6a0,
          intensity: 42,
          distanceM: 17,
        }),
        Object.freeze({
          id: "stair-fill",
          positionLocalM: { x: 0, y: 4.2, z: -1.2 },
          color: 0xe8c394,
          intensity: 30,
          distanceM: 12,
        }),
      ]),
    }),
    Object.freeze({
      id: "gedimino-9-atrium",
      label: "Gedimino 9 shopping atrium",
      landmarkId: VILNIUS_GEDIMINO_9_COLLISION.landmarkId,
      origin: VILNIUS_GEDIMINO_9_COLLISION.origin,
      rotationYRadians: VILNIUS_GEDIMINO_9_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          // The grounded character position resolves a few centimetres above
          // the authored floor, while entry hysteresis deliberately shrinks
          // the volume. Sink the lower bound below the slab so the trigger
          // still contains a correctly grounded player.
          centerLocalM: { x: -4, y: 2.3, z: 36.5 },
          halfSizeM: { x: 23.6, y: 2.5, z: 15.1 },
        }),
        Object.freeze({
          centerLocalM: { x: -4, y: 2.1, z: 53.5 },
          halfSizeM: { x: 3.85, y: 2.3, z: 2.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.3, z: 7 },
          halfSizeM: { x: 27.6, y: 2.5, z: 13.6 },
        }),
        Object.freeze({
          centerLocalM: { x: -4, y: 7.25, z: 23 },
          halfSizeM: { x: 23.6, y: 2.25, z: 27.5 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_GEDIMINO_9_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.5,
      exposureScale: 1.18,
      pointLights: Object.freeze([
        Object.freeze({
          id: "avenue-entrance-fill",
          positionLocalM: { x: -4, y: 4.2, z: 45 },
          color: 0xffddae,
          intensity: 58,
          distanceM: 22,
        }),
        Object.freeze({
          id: "chandelier-fill",
          positionLocalM: { x: -4, y: 12.5, z: 28.5 },
          color: 0xffc878,
          intensity: 64,
          distanceM: 24,
        }),
        Object.freeze({
          id: "atrium-west-fill",
          positionLocalM: { x: -14, y: 7.5, z: 23 },
          color: 0xffe6bc,
          intensity: 60,
          distanceM: 26,
        }),
        Object.freeze({
          id: "atrium-east-fill",
          positionLocalM: { x: 6, y: 7.5, z: 23 },
          color: 0xffe6bc,
          intensity: 60,
          distanceM: 26,
        }),
        Object.freeze({
          id: "gallery-west-fill",
          positionLocalM: { x: -23, y: 8.2, z: 18 },
          color: 0xf5cf94,
          intensity: 48,
          distanceM: 20,
        }),
        Object.freeze({
          id: "gallery-east-fill",
          positionLocalM: { x: 16, y: 8.2, z: 18 },
          color: 0xf5cf94,
          intensity: 48,
          distanceM: 20,
        }),
        Object.freeze({
          id: "skylight-fill",
          positionLocalM: { x: 0, y: 14.5, z: -4 },
          color: 0xd9eff2,
          intensity: 72,
          distanceM: 34,
        }),
      ]),
    }),
    Object.freeze({
      id: "baltas-tiltas-food-hall",
      label: "Baltas Tiltas food hall",
      landmarkId: VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.landmarkId,
      origin: VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.origin,
      rotationYRadians:
        VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 77.8, y: 1.9, z: 0 },
          halfSizeM: { x: 2.0, y: 2.35, z: 2.7 },
        }),
        Object.freeze({
          centerLocalM: { x: 85.0, y: 1.9, z: 0 },
          halfSizeM: { x: 8.0, y: 2.35, z: 8.5 },
        }),
        ...([-13.7, 13.7] as const).map((z) =>
          Object.freeze({
            centerLocalM: { x: 84.6, y: 1.9, z },
            halfSizeM: { x: 6.7, y: 2.35, z: 5.8 },
          })
        ),
        ...([-19.3, 19.3] as const).map((z) =>
          Object.freeze({
            centerLocalM: { x: 85.4, y: 1.9, z },
            halfSizeM: { x: 4.6, y: 2.35, z: 2.4 },
          })
        ),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_WHITE_BRIDGE_FOOD_HALL_COLLISION),
      ]),
      acousticProfile: "food-hall",
      exteriorLightScale: 0.08,
      exposureScale: 0.78,
      cameraDistanceM: 4.2,
      ambientExclusionVolumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 85.0, z: 0 },
          halfSizeM: { x: 10.4, z: 22.3 },
        }),
        Object.freeze({
          centerLocalM: { x: 73.9, z: 0 },
          halfSizeM: { x: 4.9, z: 3.25 },
        }),
      ]),
      ambientOccupants: Object.freeze([
        Object.freeze({
          id: "bar-staff",
          name: "Bar staff",
          role: "working the coffee island",
          positionLocalM: { x: 83.75, y: -0.087, z: -1.45 },
          yawRadians: -Math.PI / 2,
          activity: "working",
          clothing: { coat: 0x39433f, accent: 0xb77a45 },
        }),
        Object.freeze({
          id: "kitchen-staff",
          name: "Kitchen staff",
          role: "working the open kitchen",
          positionLocalM: { x: 91.25, y: -0.087, z: -10.7 },
          yawRadians: -Math.PI / 2,
          activity: "working",
          clothing: { coat: 0x4a4540, accent: 0xd3c7a8 },
        }),
        Object.freeze({
          id: "bar-patron",
          name: "Food hall patron",
          role: "having coffee at the bar",
          positionLocalM: { x: 79.78, y: -0.087, z: 2.35 },
          yawRadians: Math.PI / 2,
          activity: "resting",
          clothing: { coat: 0x59635f, accent: 0xa87658 },
        }),
        Object.freeze({
          id: "south-table-patron",
          name: "Food hall patron",
          role: "sharing a table",
          positionLocalM: { x: 79.25, y: -0.087, z: -15.0 },
          yawRadians: Math.PI,
          activity: "resting",
          clothing: { coat: 0x66545d, accent: 0x87969b },
        }),
        Object.freeze({
          id: "north-banquette-patron",
          name: "Food hall patron",
          role: "sitting by the windows",
          positionLocalM: { x: 77.72, y: -0.087, z: 11.25 },
          yawRadians: Math.PI / 2,
          activity: "resting",
          clothing: { coat: 0x53615a, accent: 0xb08a5d },
        }),
        Object.freeze({
          id: "north-table-patron",
          name: "Food hall patron",
          role: "having lunch",
          positionLocalM: { x: 80.45, y: -0.087, z: 8.25 },
          yawRadians: 0,
          activity: "resting",
          clothing: { coat: 0x4e5961, accent: 0x9b7566 },
        }),
      ]),
      pointLights: Object.freeze([
        Object.freeze({
          id: "south-kitchens",
          positionLocalM: { x: 88.7, y: 2.75, z: -11 },
          color: 0xffb873,
          intensity: 24,
          distanceM: 18,
        }),
        Object.freeze({
          id: "bar-coffee-island",
          positionLocalM: { x: 82.9, y: 2.7, z: 0 },
          color: 0xffc27f,
          intensity: 30,
          distanceM: 17,
        }),
        Object.freeze({
          id: "north-kitchens",
          positionLocalM: { x: 88.7, y: 2.75, z: 11 },
          color: 0xffb873,
          intensity: 24,
          distanceM: 18,
        }),
        Object.freeze({
          id: "terrace-entry",
          positionLocalM: { x: 78.3, y: 2.6, z: 0 },
          color: 0xffdfad,
          intensity: 20,
          distanceM: 13,
        }),
      ]),
    }),
    Object.freeze({
      id: "hales-market-hall",
      label: "Halės Market traditional hall and food galleries",
      landmarkId: VILNIUS_HALES_MARKET_COLLISION.landmarkId,
      origin: VILNIUS_HALES_MARKET_COLLISION.origin,
      rotationYRadians: VILNIUS_HALES_MARKET_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        // Seven overlapping room-scale boxes follow the diagonal ground hall
        // without treating the Pylimo pavement outside its tapered outline as
        // interior. Their lower faces remain beneath the surveyed floor slab.
        ...([
          [-38.37, 21.08],
          [-25.58, 15.39],
          [-12.79, 9.69],
          [0, 4],
          [12.79, -1.69],
          [25.58, -7.39],
          [38.37, -13.08],
        ] as const).map(([x, z]) =>
          Object.freeze({
            centerLocalM: { x, y: 2.4, z },
            halfSizeM: { x: 9.1, y: 2.7, z: 9.1 },
          })
        ),
        Object.freeze({
          centerLocalM: { x: -40.55, y: 1.35, z: 51.47 },
          halfSizeM: { x: 5.2, y: 3.05, z: 6.3 },
        }),
        Object.freeze({
          centerLocalM: { x: -39.5, y: 1.35, z: 38 },
          halfSizeM: { x: 6.1, y: 3.05, z: 8.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 65.35, y: 1.35, z: -24.5 },
          halfSizeM: { x: 4.1, y: 3.05, z: 5.4 },
        }),
        Object.freeze({
          centerLocalM: { x: 54, y: 1.35, z: -18.5 },
          halfSizeM: { x: 8.2, y: 3.05, z: 6.1 },
        }),
        // Two continuous mezzanine ribbons and the bridge keep the active
        // interior stable while walking between bars, cafés and restaurants.
        ...([
          [-31.05, 37.53],
          [-18.26, 31.83],
          [-5.47, 26.14],
          [7.32, 20.44],
          [20.11, 14.75],
          [32.9, 9.06],
          [45.69, 3.36],
          [-45.69, 4.64],
          [-32.9, -1.06],
          [-20.11, -6.75],
          [-7.32, -12.44],
          [5.47, -18.14],
          [18.26, -23.83],
          [31.05, -29.53],
        ] as const).map(([x, z]) =>
          Object.freeze({
            centerLocalM: { x, y: 7.55, z },
            halfSizeM: { x: 9.1, y: 2.25, z: 5.15 },
          })
        ),
        Object.freeze({
          centerLocalM: { x: -36.09, y: 7.55, z: 20.06 },
          halfSizeM: { x: 5.1, y: 2.25, z: 18.2 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_HALES_MARKET_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.58,
      exposureScale: 1.28,
      pointLights: Object.freeze([
        Object.freeze({
          id: "pylimo-entry",
          positionLocalM: { x: -37, y: 5.2, z: 43 },
          color: 0xffd39a,
          intensity: 520,
          distanceM: 28,
        }),
        Object.freeze({
          id: "produce-hall-west",
          positionLocalM: { x: -27, y: 8.8, z: 15 },
          color: 0xffe2b5,
          intensity: 820,
          distanceM: 34,
        }),
        Object.freeze({
          id: "produce-hall-centre",
          positionLocalM: { x: 0, y: 9.5, z: 4 },
          color: 0xffe8c4,
          intensity: 1_050,
          distanceM: 39,
        }),
        Object.freeze({
          id: "butcher-hall-east",
          positionLocalM: { x: 28, y: 8.8, z: -8 },
          color: 0xffd4a2,
          intensity: 820,
          distanceM: 34,
        }),
        Object.freeze({
          id: "bukowski-gallery",
          positionLocalM: { x: 14, y: 8.3, z: 24 },
          color: 0xffad69,
          intensity: 620,
          distanceM: 25,
        }),
        Object.freeze({
          id: "oldskul-gallery",
          positionLocalM: { x: -24, y: 8.3, z: 33 },
          color: 0xffc683,
          intensity: 540,
          distanceM: 23,
        }),
        Object.freeze({
          id: "petro-gallery",
          positionLocalM: { x: 16, y: 8.3, z: -22 },
          color: 0xffd99b,
          intensity: 620,
          distanceM: 25,
        }),
        Object.freeze({
          id: "onigiri-gallery",
          positionLocalM: { x: -20, y: 8.3, z: -7 },
          color: 0xffc590,
          intensity: 540,
          distanceM: 23,
        }),
        Object.freeze({
          id: "market-clock",
          positionLocalM: { x: -2, y: 6.2, z: 5 },
          color: 0xffbd70,
          intensity: 460,
          distanceM: 22,
        }),
      ]),
    }),
    Object.freeze({
      id: "mindaugo-maxima-supermarket",
      label: "Mindaugo Maxima supermarket",
      landmarkId: VILNIUS_MINDAUGO_MAXIMA_COLLISION.landmarkId,
      origin: VILNIUS_MINDAUGO_MAXIMA_COLLISION.origin,
      rotationYRadians: VILNIUS_MINDAUGO_MAXIMA_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 28, y: 2.5, z: 56.3 },
          halfSizeM: { x: 7.35, y: 2.75, z: 2.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.5, z: 35 },
          halfSizeM: { x: 60.2, y: 2.75, z: 22.8 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_MINDAUGO_MAXIMA_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.28,
      exposureScale: 1.05,
      pointLights: Object.freeze([
        Object.freeze({
          id: "entry-scan-and-go",
          positionLocalM: { x: 28, y: 4.4, z: 53 },
          color: 0xfff0d2,
          intensity: 190,
          distanceM: 28,
        }),
        Object.freeze({
          id: "staffed-checkouts",
          positionLocalM: { x: -34, y: 4.35, z: 47 },
          color: 0xffe7c2,
          intensity: 240,
          distanceM: 34,
        }),
        Object.freeze({
          id: "self-checkouts",
          positionLocalM: { x: 0, y: 4.35, z: 46 },
          color: 0xe5f0ff,
          intensity: 220,
          distanceM: 31,
        }),
        Object.freeze({
          id: "produce",
          positionLocalM: { x: 44, y: 4.35, z: 34 },
          color: 0xffe1ad,
          intensity: 220,
          distanceM: 30,
        }),
        Object.freeze({
          id: "grocery-aisles-west",
          positionLocalM: { x: -28, y: 4.35, z: 27 },
          color: 0xf5f2df,
          intensity: 270,
          distanceM: 38,
        }),
        Object.freeze({
          id: "grocery-aisles-east",
          positionLocalM: { x: 20, y: 4.35, z: 27 },
          color: 0xf5f2df,
          intensity: 270,
          distanceM: 38,
        }),
        Object.freeze({
          id: "fresh-food-counters",
          positionLocalM: { x: 0, y: 4.35, z: 15 },
          color: 0xffd09c,
          intensity: 260,
          distanceM: 39,
        }),
      ]),
    }),
    Object.freeze({
      id: "demokratine-mokykla-foyer",
      label: "Demokratinė mokykla lower floor",
      landmarkId: VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION.landmarkId,
      origin: VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION.origin,
      rotationYRadians:
        VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: -10.81, y: 2.15, z: -2.23 },
          halfSizeM: { x: 0.55, y: 2.35, z: 0.70 },
        }),
        Object.freeze({
          centerLocalM: { x: -6.35, y: 2.35, z: -1.09 },
          halfSizeM: { x: 5.0, y: 2.55, z: 1.70 },
        }),
        Object.freeze({
          centerLocalM: { x: 7, y: 2.35, z: -4.3 },
          halfSizeM: { x: 8.4, y: 2.55, z: 5.3 },
        }),
        Object.freeze({
          centerLocalM: { x: 1.8, y: 2.35, z: 2.8 },
          halfSizeM: { x: 5.6, y: 2.55, z: 3.8 },
        }),
        Object.freeze({
          centerLocalM: { x: 6.6, y: 3.0, z: 0.2 },
          halfSizeM: { x: 4.2, y: 5.2, z: 4.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 3.5, y: 4.8, z: 0.9 },
          halfSizeM: { x: 2.4, y: 1.0, z: 1.8 },
        }),
        Object.freeze({
          centerLocalM: { x: -5, y: 2.35, z: -5 },
          halfSizeM: { x: 4.0, y: 2.55, z: 3.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 2.5, y: 2.35, z: -6.5 },
          halfSizeM: { x: 4.0, y: 2.55, z: 3.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 12.25, y: 2.35, z: -4.5 },
          halfSizeM: { x: 2.75, y: 2.55, z: 4.5 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_DEMOKRATINE_MOKYKLA_COLLISION),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.30,
      exposureScale: 0.96,
      cameraDistanceM: 2.8,
      pointLights: Object.freeze([
        Object.freeze({
          id: "river-threshold",
          positionLocalM: { x: -8.68, y: 3.5, z: -1.68 },
          color: 0xffdfae,
          intensity: 30,
          distanceM: 15,
        }),
        Object.freeze({
          id: "foyer-logo-wall",
          positionLocalM: { x: 0, y: 3.9, z: 1.8 },
          color: 0xffe7c2,
          intensity: 34,
          distanceM: 17,
        }),
        Object.freeze({
          id: "curved-stair",
          positionLocalM: { x: 5, y: 5.7, z: 2.1 },
          color: 0xf2c890,
          intensity: 32,
          distanceM: 16,
        }),
        Object.freeze({
          id: "primary-classroom",
          positionLocalM: { x: -5, y: 3.8, z: -5.6 },
          color: 0xffddb0,
          intensity: 36,
          distanceM: 13,
        }),
        Object.freeze({
          id: "project-classroom",
          positionLocalM: { x: 2.5, y: 3.8, z: -6.6 },
          color: 0xffe2bd,
          intensity: 38,
          distanceM: 14,
        }),
        Object.freeze({
          id: "demo-cafe",
          positionLocalM: { x: 12.25, y: 3.7, z: -4.6 },
          color: 0xffc784,
          intensity: 42,
          distanceM: 15,
        }),
      ]),
    }),
    Object.freeze({
      id: "salomejos-neries-gimnazija-ground-floor",
      label: "Vilniaus „Vyčio“ gimnazija teaching floors",
      landmarkId:
        VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION.landmarkId,
      origin: VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION.origin,
      rotationYRadians:
        VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 17.1, y: 3.65, z: 18.0 },
          halfSizeM: { x: 4.65, y: 2.25, z: 1.35 },
        }),
        Object.freeze({
          centerLocalM: { x: 17, y: 3.75, z: 14.55 },
          halfSizeM: { x: 4.2, y: 2.35, z: 4.2 },
        }),
        Object.freeze({
          centerLocalM: { x: -2.8, y: 3.75, z: 9.35 },
          halfSizeM: { x: 19.5, y: 2.35, z: 1.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 6, y: 3.75, z: 14.2 },
          halfSizeM: { x: 5, y: 2.35, z: 3.4 },
        }),
        Object.freeze({
          centerLocalM: { x: -6, y: 3.75, z: 14 },
          halfSizeM: { x: 5.5, y: 2.35, z: 3.4 },
        }),
        Object.freeze({
          centerLocalM: { x: -18.2, y: 3.75, z: 13.8 },
          halfSizeM: { x: 5.2, y: 2.35, z: 3.4 },
        }),
        Object.freeze({
          centerLocalM: { x: 3, y: 2.95, z: -5.7 },
          halfSizeM: { x: 2.9, y: 1.4, z: 14.7 },
        }),
        Object.freeze({
          centerLocalM: { x: -0.7, y: 7.05, z: -21.35 },
          halfSizeM: { x: 21.8, y: 5.65, z: 12.05 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(
          VILNIUS_SALOMEJOS_NERIES_GIMNAZIJA_COLLISION,
        ),
      ]),
      acousticProfile: "civic-lobby",
      exteriorLightScale: 0.28,
      exposureScale: 0.96,
      cameraDistanceM: 1.8,
      pointLights: Object.freeze([
        Object.freeze({
          id: "river-threshold",
          positionLocalM: { x: 17.2, y: 5.05, z: 17.2 },
          color: 0xffdfb5,
          intensity: 36,
          distanceM: 15,
        }),
        Object.freeze({
          id: "reception",
          positionLocalM: { x: 14.5, y: 5.15, z: 12.8 },
          color: 0xffd5a3,
          intensity: 40,
          distanceM: 16,
        }),
        Object.freeze({
          id: "display-corridor-east",
          positionLocalM: { x: 5.5, y: 5.15, z: 9.4 },
          color: 0xffe9c6,
          intensity: 34,
          distanceM: 17,
        }),
        Object.freeze({
          id: "display-corridor-west",
          positionLocalM: { x: -13.5, y: 5.15, z: 9.4 },
          color: 0xffe9c6,
          intensity: 34,
          distanceM: 17,
        }),
        Object.freeze({
          id: "general-classroom",
          positionLocalM: { x: 6, y: 5.25, z: 14.2 },
          color: 0xffe3bd,
          intensity: 42,
          distanceM: 14,
        }),
        Object.freeze({
          id: "media-classroom",
          positionLocalM: { x: -6, y: 5.25, z: 14 },
          color: 0xd8e4ff,
          intensity: 44,
          distanceM: 14,
        }),
        Object.freeze({
          id: "project-room",
          positionLocalM: { x: -18.2, y: 5.25, z: 13.8 },
          color: 0xffd6a0,
          intensity: 42,
          distanceM: 14,
        }),
        ...([0, 1, 2, 3] as const).flatMap((levelIndex) => {
          const y = 3.85 + levelIndex * 2.8;
          return [
            Object.freeze({
              id: `historic-level-${levelIndex + 1}-west`,
              positionLocalM: { x: -12, y, z: -21.35 },
              color: levelIndex % 2 === 0 ? 0xffe3bd : 0xd8e4ff,
              intensity: 38,
              distanceM: 16,
            }),
            Object.freeze({
              id: `historic-level-${levelIndex + 1}-east`,
              positionLocalM: { x: 10, y, z: -21.35 },
              color: levelIndex % 2 === 0 ? 0xd8e4ff : 0xffe3bd,
              intensity: 38,
              distanceM: 16,
            }),
          ];
        }),
      ]),
    }),
    Object.freeze({
      id: "kablys-main-hall",
      label: "Kablys lobby, great hall and balcony",
      landmarkId: VILNIUS_KABLYS_COLLISION.landmarkId,
      origin: VILNIUS_KABLYS_COLLISION.origin,
      rotationYRadians: VILNIUS_KABLYS_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 3.2, z: 18 },
          halfSizeM: { x: 16.8, y: 3.5, z: 7.4 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 3.2, z: -7.2 },
          halfSizeM: { x: 16.8, y: 3.5, z: 18.1 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 7.1, z: 5.9 },
          halfSizeM: { x: 16.8, y: 1.9, z: 3.55 },
        }),
        ...([-1, 1] as const).map((side) =>
          Object.freeze({
            centerLocalM: { x: side * 10.8, y: 4.2, z: 2.8 },
            halfSizeM: { x: 2.25, y: 3.1, z: 5.25 },
          })
        ),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_KABLYS_COLLISION),
      ]),
      acousticProfile: "music-venue",
      exteriorLightScale: 0.2,
      exposureScale: 1.16,
      pointLights: Object.freeze([
        Object.freeze({
          id: "portico-threshold",
          positionLocalM: { x: 0, y: 5.3, z: 22.4 },
          color: 0xffc787,
          intensity: 180,
          distanceM: 18,
        }),
        Object.freeze({
          id: "lobby-bar",
          positionLocalM: { x: -8.5, y: 5.6, z: 17.2 },
          color: 0xffa55d,
          intensity: 210,
          distanceM: 19,
        }),
        Object.freeze({
          id: "great-hall-ring",
          positionLocalM: { x: 0, y: 9.7, z: -9.2 },
          color: 0xffd29a,
          intensity: 320,
          distanceM: 31,
        }),
        Object.freeze({
          id: "stage-wash",
          positionLocalM: { x: 0, y: 5.8, z: -25.5 },
          color: 0xb84331,
          intensity: 260,
          distanceM: 24,
        }),
        Object.freeze({
          id: "balcony-red-fill",
          positionLocalM: { x: 0, y: 8.6, z: 5.5 },
          color: 0x9d3c32,
          intensity: 170,
          distanceM: 22,
        }),
      ]),
    }),
    Object.freeze({
      id: "lietuvos-bankas-historic-vault",
      label: "Bank of Lithuania historic palace and vault",
      landmarkId: VILNIUS_LIETUVOS_BANKAS_COLLISION.landmarkId,
      origin: VILNIUS_LIETUVOS_BANKAS_COLLISION.origin,
      rotationYRadians:
        VILNIUS_LIETUVOS_BANKAS_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 4.5, y: 2.5, z: -3 },
          halfSizeM: { x: 27.2, y: 2.5, z: 19.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 4.5, y: 2.5, z: -22.6 },
          halfSizeM: { x: 1.85, y: 2.5, z: 1.15 },
        }),
        Object.freeze({
          centerLocalM: { x: -11, y: 2.45, z: 30 },
          halfSizeM: { x: 5.6, y: 2.45, z: 13.8 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_LIETUVOS_BANKAS_COLLISION),
      ]),
      acousticProfile: "historic-bank",
      exteriorLightScale: 0.2,
      exposureScale: 1.1,
      pointLights: Object.freeze([
        Object.freeze({
          id: "vestibule-clock-fill",
          positionLocalM: { x: 4.5, y: 3.7, z: -18.8 },
          color: 0xffd19b,
          intensity: 38,
          distanceM: 13,
        }),
        Object.freeze({
          id: "stair-hall-fill",
          positionLocalM: { x: 4.5, y: 5.4, z: -2 },
          color: 0xffe1b2,
          intensity: 52,
          distanceM: 25,
        }),
        Object.freeze({
          id: "ceramic-furnace-corridor-fill",
          positionLocalM: { x: -14.8, y: 4.2, z: -5.5 },
          color: 0xe8cfaa,
          intensity: 32,
          distanceM: 14,
        }),
        Object.freeze({
          id: "stained-glass-fill",
          positionLocalM: { x: 18.8, y: 4.1, z: 15.5 },
          color: 0xf1bd75,
          intensity: 34,
          distanceM: 14,
        }),
        Object.freeze({
          id: "historic-vault-fill",
          positionLocalM: { x: -11, y: 3.4, z: 37.5 },
          color: 0xd6c7aa,
          intensity: 30,
          distanceM: 15,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-bus-station-hall",
      label: "Vilnius Bus Station passenger hall",
      landmarkId: VILNIUS_BUS_STATION_COLLISION.landmarkId,
      origin: VILNIUS_BUS_STATION_COLLISION.origin,
      rotationYRadians: VILNIUS_BUS_STATION_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.0, z: 0.5 },
          halfSizeM: { x: 32.8, y: 3.0, z: 19.8 },
        }),
        Object.freeze({
          centerLocalM: { x: 5, y: 2.0, z: 19.2 },
          halfSizeM: { x: 3.7, y: 3.0, z: 2.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 5, y: 2.0, z: -22.0 },
          halfSizeM: { x: 3.7, y: 3.0, z: 1.9 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_BUS_STATION_COLLISION),
      ]),
      acousticProfile: "bus-terminal",
      exteriorLightScale: 0.3,
      exposureScale: 1.15,
      pointLights: Object.freeze([
        Object.freeze({
          id: "platform-entry-fill",
          positionLocalM: { x: 5, y: 4.8, z: 14.5 },
          color: 0xffd59c,
          intensity: 48,
          distanceM: 19,
        }),
        Object.freeze({
          id: "waiting-hall-fill",
          positionLocalM: { x: -8, y: 6.8, z: 0 },
          color: 0xffe6bc,
          intensity: 68,
          distanceM: 25,
        }),
        Object.freeze({
          id: "ticket-office-fill",
          positionLocalM: { x: 24, y: 4.2, z: -1 },
          color: 0xffc97f,
          intensity: 42,
          distanceM: 16,
        }),
        Object.freeze({
          id: "city-entry-fill",
          positionLocalM: { x: 5, y: 4.5, z: -16 },
          color: 0xd8e4dc,
          intensity: 38,
          distanceM: 17,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-railway-station-hall",
      label: "Vilnius Railway Station main hall",
      landmarkId: VILNIUS_RAILWAY_STATION_COLLISION.landmarkId,
      origin: VILNIUS_RAILWAY_STATION_COLLISION.origin,
      rotationYRadians:
        VILNIUS_RAILWAY_STATION_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          // The GRPK terrain at the hall axis sits roughly 0.3 m below the
          // landmark origin. Keep the lower trigger face beneath that sampled
          // ground even after entry hysteresis shrinks the volume.
          centerLocalM: { x: 0, y: 2.35, z: 0 },
          halfSizeM: { x: 16.15, y: 3.1, z: 19.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.15, z: 21.8 },
          halfSizeM: { x: 3.1, y: 2.95, z: 2.6 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.15, z: -21.7 },
          halfSizeM: { x: 3.1, y: 2.95, z: 2.7 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_RAILWAY_STATION_COLLISION),
      ]),
      acousticProfile: "railway-hall",
      exteriorLightScale: 0.32,
      exposureScale: 1.18,
      pointLights: Object.freeze([
        Object.freeze({
          id: "forecourt-entry-fill",
          positionLocalM: { x: 0, y: 5.2, z: 15 },
          color: 0xffdfb0,
          intensity: 44,
          distanceM: 21,
        }),
        Object.freeze({
          id: "main-hall-fill",
          positionLocalM: { x: 0, y: 9.5, z: 1 },
          color: 0xffe7c5,
          intensity: 76,
          distanceM: 27,
        }),
        Object.freeze({
          id: "ticket-hall-fill",
          positionLocalM: { x: 11.5, y: 4.5, z: 4 },
          color: 0xffd39a,
          intensity: 36,
          distanceM: 15,
        }),
        Object.freeze({
          id: "west-waiting-fill",
          positionLocalM: { x: -10.5, y: 4.5, z: 3 },
          color: 0xffd9a6,
          intensity: 34,
          distanceM: 15,
        }),
        Object.freeze({
          id: "platform-door-fill",
          positionLocalM: { x: 0, y: 4.5, z: -16 },
          color: 0xd7e5e2,
          intensity: 40,
          distanceM: 18,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-airport-new-departures-hall",
      label: "Vilnius Airport 2025 departures terminal",
      landmarkId: VILNIUS_AIRPORT_TERMINAL_COLLISION.landmarkId,
      origin: VILNIUS_AIRPORT_TERMINAL_COLLISION.origin,
      rotationYRadians:
        VILNIUS_AIRPORT_TERMINAL_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 3.2, z: 0 },
          halfSizeM: { x: 55.8, y: 3.5, z: 40.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 28, y: 10.2, z: 0 },
          halfSizeM: { x: 26.5, y: 3.0, z: 36.5 },
        }),
        Object.freeze({
          centerLocalM: { x: -58, y: 2.7, z: 0 },
          halfSizeM: { x: 2.4, y: 3.0, z: 6.7 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_AIRPORT_TERMINAL_COLLISION),
      ]),
      acousticProfile: "airport-terminal",
      exteriorLightScale: 0.30,
      exposureScale: 1.10,
      pointLights: Object.freeze([
        Object.freeze({
          id: "departures-entry",
          positionLocalM: { x: -42, y: 5.8, z: 0 },
          color: 0xf6ead8,
          intensity: 250,
          distanceM: 20,
        }),
        Object.freeze({
          id: "departures-check-in-north",
          positionLocalM: { x: -24, y: 7.6, z: -22 },
          color: 0xffe8c7,
          intensity: 460,
          distanceM: 20,
        }),
        Object.freeze({
          id: "departures-check-in-south",
          positionLocalM: { x: -24, y: 7.6, z: 22 },
          color: 0xffe8c7,
          intensity: 460,
          distanceM: 20,
        }),
        Object.freeze({
          id: "departures-security",
          positionLocalM: { x: 28, y: 13.0, z: 0 },
          color: 0xd9edee,
          intensity: 450,
          distanceM: 23,
        }),
        Object.freeze({
          id: "departures-cafe",
          positionLocalM: { x: 4, y: 5.4, z: -32 },
          color: 0xffc987,
          intensity: 340,
          distanceM: 18,
        }),
        Object.freeze({
          id: "departures-cultural-gallery",
          positionLocalM: { x: 51, y: 12.0, z: 0 },
          color: 0xa7c6b8,
          intensity: 220,
          distanceM: 17,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-airport-existing-terminals",
      label: "Vilnius Airport T2-T3 and historic arrivals hall",
      landmarkId: VILNIUS_AIRPORT_TERMINAL_COLLISION.landmarkId,
      origin: VILNIUS_AIRPORT_TERMINAL_COLLISION.origin,
      rotationYRadians:
        VILNIUS_AIRPORT_TERMINAL_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.8, z: 60 },
          halfSizeM: { x: 17.5, y: 3.1, z: 19.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 24, y: 3.0, z: 121 },
          halfSizeM: { x: 70.5, y: 3.4, z: 41.5 },
        }),
        Object.freeze({
          centerLocalM: { x: 24, y: 2.8, z: 165.75 },
          halfSizeM: { x: 6.7, y: 3.1, z: 3.3 },
        }),
        Object.freeze({
          centerLocalM: { x: 24, y: 3.0, z: 184 },
          halfSizeM: { x: 45.5, y: 3.4, z: 15.0 },
        }),
        Object.freeze({
          centerLocalM: { x: 24, y: 2.7, z: 202 },
          halfSizeM: { x: 4.3, y: 3.0, z: 3.1 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_AIRPORT_TERMINAL_COLLISION),
      ]),
      acousticProfile: "airport-terminal",
      exteriorLightScale: 0.24,
      exposureScale: 1.08,
      pointLights: Object.freeze([
        Object.freeze({
          id: "connector",
          positionLocalM: { x: 0, y: 6.0, z: 60 },
          color: 0xe1eeeb,
          intensity: 260,
          distanceM: 22,
        }),
        Object.freeze({
          id: "vno-plaza",
          positionLocalM: { x: 6, y: 7.8, z: 110 },
          color: 0xffdfb6,
          intensity: 430,
          distanceM: 25,
        }),
        Object.freeze({
          id: "baggage-reclaim",
          positionLocalM: { x: 76, y: 6.8, z: 136 },
          color: 0xd9e5e3,
          intensity: 340,
          distanceM: 22,
        }),
        Object.freeze({
          id: "historic-hall",
          positionLocalM: { x: 24, y: 8.4, z: 184 },
          color: 0xffd8a5,
          intensity: 470,
          distanceM: 22,
        }),
        Object.freeze({
          id: "vno-plaza-cafe",
          positionLocalM: { x: 16, y: 6.8, z: 118 },
          color: 0xffbf73,
          intensity: 340,
          distanceM: 18,
        }),
        Object.freeze({
          id: "historic-information-desk",
          positionLocalM: { x: 4, y: 4.8, z: 184 },
          color: 0xffc47d,
          intensity: 260,
          distanceM: 15,
        }),
        Object.freeze({
          id: "historic-cafe",
          positionLocalM: { x: 44, y: 4.8, z: 184 },
          color: 0xffae62,
          intensity: 260,
          distanceM: 15,
        }),
      ]),
    }),
    Object.freeze({
      id: "lukiskes-prison-cellblock",
      label: "Lukiškės Prison cellblock",
      landmarkId: VILNIUS_LUKISKES_PRISON_COLLISION.landmarkId,
      origin: VILNIUS_LUKISKES_PRISON_COLLISION.origin,
      rotationYRadians:
        VILNIUS_LUKISKES_PRISON_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: 45 },
          halfSizeM: { x: 4.8, y: 2.2, z: 8.8 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: 29.5 },
          halfSizeM: { x: 4.8, y: 2.2, z: 6.3 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: 19 },
          halfSizeM: { x: 8.2, y: 2.2, z: 8.2 },
        }),
        Object.freeze({
          centerLocalM: { x: 0, y: 4.8, z: -17.5 },
          halfSizeM: { x: 2.55, y: 4.8, z: 30.2 },
        }),
        ...[-1, 1].flatMap((side) =>
          [1.5, -6].map((z) =>
            Object.freeze({
              centerLocalM: { x: side * 5.8, y: 2.2, z },
              halfSizeM: { x: 2.9, y: 2.2, z: 3.4 },
            })
          )
        ),
      ]),
      cameraObstructionIds: Object.freeze(
        VILNIUS_LUKISKES_PRISON_FOOTPRINTS.map((footprint) =>
          landmarkObstructionId(footprint)
        ),
      ),
      acousticProfile: "prison-cellblock",
      exteriorLightScale: 0.18,
      exposureScale: 1.12,
      pointLights: Object.freeze([
        Object.freeze({
          id: "entrance-bulkhead",
          positionLocalM: { x: 0, y: 3.6, z: 43 },
          color: 0xe8c79a,
          intensity: 28,
          distanceM: 13,
        }),
        Object.freeze({
          id: "radial-hub",
          positionLocalM: { x: 0, y: 4.4, z: 18 },
          color: 0xe0c28f,
          intensity: 34,
          distanceM: 16,
        }),
        Object.freeze({
          id: "cellblock-front",
          positionLocalM: { x: 0, y: 6.8, z: 6 },
          color: 0xc8d5c6,
          intensity: 30,
          distanceM: 21,
        }),
        Object.freeze({
          id: "cellblock-middle",
          positionLocalM: { x: 0, y: 6.8, z: -19 },
          color: 0xc8d5c6,
          intensity: 32,
          distanceM: 22,
        }),
        Object.freeze({
          id: "cellblock-end-window",
          positionLocalM: { x: 0, y: 6.2, z: -42 },
          color: 0xbacdcc,
          intensity: 35,
          distanceM: 18,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-tv-tower-lobby",
      label: "Vilnius TV Tower ground-floor exhibition",
      landmarkId: VILNIUS_TV_TOWER_COLLISION.landmarkId,
      origin: VILNIUS_TV_TOWER_COLLISION.origin,
      rotationYRadians: VILNIUS_TV_TOWER_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 2.2, z: 24 },
          halfSizeM: { x: 5.65, y: 2.2, z: 5.65 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_TV_TOWER_COLLISION),
      ]),
      acousticProfile: "tower-lobby",
      exteriorLightScale: 0.3,
      exposureScale: 1.08,
      pointLights: Object.freeze([
        Object.freeze({
          id: "memorial-exhibition-fill",
          positionLocalM: { x: -2.9, y: 3.3, z: 24.8 },
          color: 0xffd19a,
          intensity: 35,
          distanceM: 11,
        }),
        Object.freeze({
          id: "lift-lobby-fill",
          positionLocalM: { x: 2.5, y: 3.3, z: 21 },
          color: 0xe3c295,
          intensity: 30,
          distanceM: 10,
        }),
      ]),
    }),
    Object.freeze({
      id: "vilnius-tv-tower-observation-deck",
      label: "Vilnius TV Tower observation deck",
      landmarkId: VILNIUS_TV_TOWER_COLLISION.landmarkId,
      origin: VILNIUS_TV_TOWER_COLLISION.origin,
      rotationYRadians: VILNIUS_TV_TOWER_COLLISION.rotationYRadians,
      volumes: Object.freeze([
        Object.freeze({
          centerLocalM: { x: 0, y: 169.1, z: 0 },
          halfSizeM: { x: 17.2, y: 3.05, z: 17.2 },
        }),
      ]),
      cameraObstructionIds: Object.freeze([
        landmarkObstructionId(VILNIUS_TV_TOWER_COLLISION),
      ]),
      acousticProfile: "tower-observation",
      exteriorLightScale: 0.78,
      exposureScale: 1.02,
      pointLights: Object.freeze([
        ...[-10.5, 10.5].flatMap((x) =>
          [-7.5, 7.5].map((z) =>
            Object.freeze({
              id: `observation-${x}-${z}`,
              positionLocalM: { x, y: 171.2, z },
              color: 0xffd7a2,
              intensity: 25,
              distanceM: 13,
            })
          )
        ),
      ]),
    }),
  ]);

export const INTERIOR_STABILIZATION_CONFIG = Object.freeze({
  entryInsetM: 0.18,
  exitPaddingM: 0.85,
  entryConfirmationTicks: 4,
  exitConfirmationTicks: 8,
  lightingResponsePerSecond: 5.5,
});

export function authoredInteriorById(
  id: AuthoredInteriorId | null,
): AuthoredInteriorDefinition | null {
  if (id === null) {
    return null;
  }
  return AUTHORED_INTERIORS.find((interior) => interior.id === id) ?? null;
}

export function authoredInteriorWorldPoint(
  interior: Readonly<AuthoredInteriorDefinition>,
  local: Readonly<{ x: number; y: number; z: number }>,
): Readonly<{ x: number; y: number; z: number }> {
  const cosine = Math.cos(interior.rotationYRadians);
  const sine = Math.sin(interior.rotationYRadians);
  return {
    x: interior.origin.x + local.x * cosine + local.z * sine,
    y: interior.origin.y + local.y,
    z: interior.origin.z - local.x * sine + local.z * cosine,
  };
}

export function authoredInteriorLocalPoint(
  interior: Readonly<AuthoredInteriorDefinition>,
  world: Readonly<{ x: number; y: number; z: number }>,
): Readonly<{ x: number; y: number; z: number }> {
  const cosine = Math.cos(interior.rotationYRadians);
  const sine = Math.sin(interior.rotationYRadians);
  const deltaX = world.x - interior.origin.x;
  const deltaZ = world.z - interior.origin.z;
  return {
    x: deltaX * cosine - deltaZ * sine,
    y: world.y - interior.origin.y,
    z: deltaX * sine + deltaZ * cosine,
  };
}

export function authoredInteriorExcludesAmbientPosition(
  interior: Readonly<AuthoredInteriorDefinition>,
  world: Readonly<{ x: number; z: number }>,
): boolean {
  if (interior.ambientExclusionVolumes === undefined) {
    return false;
  }
  const local = authoredInteriorLocalPoint(interior, {
    ...world,
    y: interior.origin.y,
  });
  return interior.ambientExclusionVolumes.some((volume) =>
    Math.abs(local.x - volume.centerLocalM.x) <= volume.halfSizeM.x &&
    Math.abs(local.z - volume.centerLocalM.z) <= volume.halfSizeM.z
  );
}
